/**
 * Guest Wizard: Steg 1 - Företag
 *
 * Gäster definierar sitt företag innan de väljer datakälla (SIE-fil, manuell, integration).
 * Använder publik Bolagsverket-lookup (ingen autentisering krävs).
 * Organisationsnumret valideras senare mot SIE-filens data (om SIE-fil används).
 */

import { useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Building2,
  MapPin,
  Hash,
  Search,
  Loader2,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { wizardApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import type { GuestCompany } from "@/lib/guest-wizard";

const companySchema = z.object({
  organizationNumber: z
    .string()
    .min(10, "Organisationsnummer måste vara minst 10 siffror")
    .max(13, "Ogiltigt format"),
  name: z.string().min(1, "Företagsnamn krävs"),
  type: z.enum(["AB", "EF", "HB", "KB"]).default("AB"),
  seatMunicipality: z.string().optional(),
  address: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
});

type CompanyFormData = z.infer<typeof companySchema>;

interface GuestCompanyStepProps {
  /** Existing company data (if returning to this step) */
  company: GuestCompany | null;
  /** Save company and navigate to next step */
  onContinue: (company: GuestCompany) => void;
}

export function GuestCompanyStep({ company, onContinue }: GuestCompanyStepProps) {
  // Lookup state
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupComplete, setLookupComplete] = useState(!!company?.fromLookup);
  const [lookupFailed, setLookupFailed] = useState(!!company && !company.fromLookup);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: company
      ? {
          organizationNumber: company.organizationNumber,
          name: company.name,
          type: company.type,
          seatMunicipality: company.seatMunicipality || "",
          address: company.address || "",
          postalCode: company.postalCode || "",
          city: company.city || "",
        }
      : {
          type: "AB",
        },
  });

  const formUnlocked = lookupComplete || lookupFailed;

  // Lookup company from Bolagsverket (public endpoint)
  const handleLookupCompany = useCallback(
    async (orgNumber: string) => {
      const digits = orgNumber.replace(/\D/g, "");
      if (digits.length < 10) return;

      setIsLookingUp(true);
      setLookupError(null);
      setLookupFailed(false);

      try {
        const response = await wizardApi.lookupCompany(orgNumber);

        if (response.success && response.data) {
          setValue("name", response.data.name);
          setValue("type", response.data.type);
          if (response.data.seatMunicipality) {
            setValue("seatMunicipality", response.data.seatMunicipality);
          }
          if (response.data.address) {
            if (response.data.address.street) {
              setValue("address", response.data.address.street);
            }
            if (response.data.address.postalCode) {
              setValue("postalCode", response.data.address.postalCode);
            }
            if (response.data.address.city) {
              setValue("city", response.data.address.city);
            }
          }
          setLookupComplete(true);
          setLookupFailed(false);
        } else {
          setLookupFailed(true);
          setLookupError(response.error?.message || "Företaget hittades inte");
        }
      } catch (err) {
        console.error("Lookup error:", err);
        toast.error("Kunde inte ansluta till Bolagsverket");
        setLookupFailed(true);
        setLookupError("Kunde inte ansluta till Bolagsverket");
      } finally {
        setIsLookingUp(false);
      }
    },
    [setValue]
  );

  const handleContinue = (data: CompanyFormData) => {
    const guestCompany: GuestCompany = {
      organizationNumber: data.organizationNumber,
      name: data.name,
      type: data.type,
      seatMunicipality: data.seatMunicipality,
      address: data.address,
      postalCode: data.postalCode,
      city: data.city,
      fromLookup: lookupComplete,
    };
    onContinue(guestCompany);
  };

  const orgNumber = watch("organizationNumber");
  const canLookup = orgNumber && orgNumber.replace(/\D/g, "").length >= 10;

  return (
    <div className="max-w-xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Vilket företag gäller årsredovisningen?
        </h2>
        <p className="text-gray-600">
          Ange organisationsnummer så hämtar vi uppgifter från Bolagsverket.
        </p>
      </div>

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
          <div className="text-sm text-blue-800">
            <p>
              Organisationsnumret används för att identifiera företaget och verifiera
              bokföringsdata i nästa steg.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(handleContinue)} className="space-y-6">
        {/* Section: Företagsinformation */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
          <div className="flex items-center gap-2">
            <Building2 size={18} className="text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-900">
              Företagsinformation
            </h3>
          </div>

          {/* Organisationsnummer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Organisationsnummer
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Hash size={15} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  {...register("organizationNumber")}
                  className="input pl-9"
                  placeholder="XXXXXX-XXXX"
                  disabled={isLookingUp}
                />
              </div>
              <Button
                type="button"
                onClick={() => {
                  if (orgNumber) {
                    handleLookupCompany(orgNumber);
                  }
                }}
                disabled={isLookingUp || !canLookup}
              >
                {isLookingUp ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <>
                    <Search size={15} />
                    Hämta uppgifter
                  </>
                )}
              </Button>
            </div>
            {errors.organizationNumber && (
              <p className="text-sm text-red-600 mt-1">
                {errors.organizationNumber.message}
              </p>
            )}
            {lookupComplete && (
              <p className="text-sm text-green-600 mt-1.5 flex items-center gap-1">
                <CheckCircle size={14} />
                Uppgifter hämtade från Bolagsverket
              </p>
            )}
            {lookupError && (
              <p className="text-sm text-amber-600 mt-1.5 flex items-center gap-1">
                <AlertCircle size={14} />
                {lookupError}. Du kan fylla i uppgifterna manuellt.
              </p>
            )}
            {!formUnlocked && !errors.organizationNumber && (
              <p className="text-sm text-gray-500 mt-1.5">
                Ange organisationsnummer och klicka &quot;Hämta uppgifter&quot; för att fortsätta
              </p>
            )}
          </div>

          {/* Företagsnamn */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Företagsnamn *
            </label>
            <input
              type="text"
              {...register("name")}
              className="input"
              placeholder="Företaget AB"
              disabled={!formUnlocked}
            />
            {errors.name && (
              <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
            )}
          </div>

          {/* Företagstyp */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Företagstyp
            </label>
            <select
              {...register("type")}
              className="input"
              disabled={!formUnlocked}
            >
              <option value="AB">Aktiebolag (AB)</option>
              <option value="EF">Enskild firma (EF)</option>
              <option value="HB">Handelsbolag (HB)</option>
              <option value="KB">Kommanditbolag (KB)</option>
            </select>
          </div>
        </div>

        {/* Section: Adressuppgifter (valfritt) */}
        {formUnlocked && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
            <div className="flex items-center gap-2">
              <MapPin size={18} className="text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-900">
                Adressuppgifter
                <span className="font-normal text-gray-500 ml-1">(valfritt)</span>
              </h3>
            </div>

            {/* Adress */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Adress
              </label>
              <input
                type="text"
                {...register("address")}
                className="input"
                placeholder="Gatuadress"
              />
            </div>

            {/* Postnummer + Stad */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Postnummer
                </label>
                <input
                  type="text"
                  {...register("postalCode")}
                  className="input"
                  placeholder="123 45"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Stad
                </label>
                <input
                  type="text"
                  {...register("city")}
                  className="input"
                  placeholder="Stockholm"
                />
              </div>
            </div>

            {/* Län */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Län
              </label>
              <input
                type="text"
                {...register("seatMunicipality")}
                className="input"
                placeholder="Stockholms län"
              />
            </div>
          </div>
        )}

        {/* Continue button */}
        <div className="flex justify-end pt-4">
          <Button type="submit" size="lg" disabled={!formUnlocked}>
            Fortsätt till bokföringsdata
            <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </form>
    </div>
  );
}
